import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Descriptions, Space, Button, Spin, message, theme } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { PageHeader } from '../../components/PageHeader';
import { DeleteTipoServicioConfirmModal } from './components/DeleteTipoServicioConfirmModal';
import { useTipoServicio, useDeleteTipoServicio } from './api/useTiposServicio';
import { useHasPermission } from '../../auth/useHasPermission';
import { EmptyState } from '../../components/EmptyState';
import dayjs from 'dayjs';
import { Permission } from '@aid/shared';

export function TipoServicioDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const tipoServicioId = Number(id);
  const { data: tipoServicio, isLoading, error } = useTipoServicio(tipoServicioId);
  const deleteTipoServicio = useDeleteTipoServicio();
  const canUpdate = useHasPermission(Permission.TIPOS_SERVICIO_UPDATE);
  const canDelete = useHasPermission(Permission.TIPOS_SERVICIO_DELETE);
  const { token } = theme.useToken();

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !tipoServicio) {
    return (
      <EmptyState
        title="Tipo de servicio no encontrado"
        description="El tipo de servicio que buscas no existe o fue eliminado."
      />
    );
  }

  const handleDelete = async () => {
    try {
      await deleteTipoServicio.mutateAsync(tipoServicio.id);
      message.success('Tipo de servicio eliminado');
      navigate('/tipos-servicio');
    } catch {
      message.error('Error al eliminar el tipo de servicio');
    }
  };

  return (
    <div>
      <PageHeader
        title={tipoServicio.nombre ?? `Tipo Servicio #${tipoServicio.id}`}
        actions={
          <Space wrap size={[8, 8]}>
            {canUpdate && (
              <Button
                icon={<EditOutlined />}
                onClick={() => navigate(`/tipos-servicio/${tipoServicio.id}/edit`)}
              >
                Editar
              </Button>
            )}
            {canDelete && (
              <Button danger icon={<DeleteOutlined />} onClick={() => setDeleteModalOpen(true)}>
                Eliminar
              </Button>
            )}
          </Space>
        }
      />
      <Card
        title="Información del tipo de servicio"
        style={{
          borderRadius: 12,
          border: `1px solid ${token.colorBorderSecondary}`,
          boxShadow: token.boxShadow,
        }}
        styles={{
          header: {
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            padding: '14px 22px',
          },
        }}
      >
        <Descriptions
          column={{ xs: 1, sm: 2 }}
          style={{ marginTop: 8 }}
          labelStyle={{
            color: token.colorTextSecondary,
            fontWeight: 500,
            paddingTop: 14,
            paddingBottom: 14,
          }}
          contentStyle={{ paddingTop: 14, paddingBottom: 14 }}
        >
          <Descriptions.Item label="ID">{tipoServicio.id}</Descriptions.Item>
          <Descriptions.Item label="Nombre">{tipoServicio.nombre ?? '-'}</Descriptions.Item>
          <Descriptions.Item label="Genera factura">
            {tipoServicio.generaCobro ? 'Sí' : 'No'}
          </Descriptions.Item>
          <Descriptions.Item label="Creado">
            {tipoServicio.fechaCreacion
              ? dayjs(tipoServicio.fechaCreacion).format('DD MMM YYYY')
              : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Modificado">
            {tipoServicio.fechaModificacion
              ? dayjs(tipoServicio.fechaModificacion).format('DD MMM YYYY')
              : '-'}
          </Descriptions.Item>
        </Descriptions>
      </Card>
      <DeleteTipoServicioConfirmModal
        open={deleteModalOpen}
        onConfirm={handleDelete}
        onCancel={() => setDeleteModalOpen(false)}
        loading={deleteTipoServicio.isPending}
      />
    </div>
  );
}
